import { useCallback } from 'react';

import { useAppState, useAppStateRaw } from '../store/app-store/app-store';
import { type ParsedToken } from '../store/app-store/types';

export enum Roles {
  USER = 'ROLE_USER',
  KASSIR = 'ROLE_KASSIR',
  TEST = 'ROLE_TEST',
  DEV = 'ROLE_DEVELOPER',
}

type FeatureLevel = 'kassir' | 'test';

const checkRole = (parsedToken: ParsedToken | null, role: Roles) => {
  if (!parsedToken?.authorities) return false;

  // Allow all features for development
  if (import.meta.env.DEV) return true;

  return parsedToken.authorities.includes(role);
};

export function useFeatureFlag() {
  const { parsedToken } = useAppState();

  const isKassir = checkRole(parsedToken, Roles.KASSIR);
  const isTestUser = isKassir || checkRole(parsedToken, Roles.TEST);
  const isDev = checkRole(parsedToken, Roles.DEV);

  const hide = useCallback(
    (level: FeatureLevel) => {
      switch (level) {
        case 'kassir':
          return isKassir;
        case 'test':
          return isKassir || isTestUser;
      }
    },
    [isKassir, isTestUser]
  );

  return { hide, isKassir, isTestUser, isDev };
}

export const getFeatureFlag = () => {
  const { parsedToken } = useAppStateRaw.getState();

  return {
    isKassir: checkRole(parsedToken, Roles.KASSIR),
    isTestUser: checkRole(parsedToken, Roles.TEST),
    isDev: checkRole(parsedToken, Roles.DEV),
  };
};
